import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import * as Sentry from '@sentry/react';
import { AppErrorBoundary } from '@/shared/errors';
import { PermissionRequestDialogHost } from '@/features/roles-and-permissions';

// Safari compatibility: some bundled libs call URL.parse / URL.canParse.
// Provide safe fallbacks on browsers that do not implement these static APIs.
(() => {
  const urlCtor = URL as typeof URL & {
    parse?: (url: string, base?: string | URL) => URL | null;
    canParse?: (url: string, base?: string | URL) => boolean;
  };

  if (typeof urlCtor.parse !== 'function') {
    urlCtor.parse = (url: string, base?: string | URL) => {
      try {
        return new URL(url, base);
      } catch {
        return null;
      }
    };
  }

  if (typeof urlCtor.canParse !== 'function') {
    urlCtor.canParse = (url: string, base?: string | URL) => {
      try {
        void new URL(url, base);
        return true;
      } catch {
        return false;
      }
    };
  }

  const mapProto = Map.prototype as any;
  if (typeof mapProto.getOrInsert !== 'function') {
    Object.defineProperty(mapProto, 'getOrInsert', {
      configurable: true,
      writable: true,
      value: function getOrInsert(this: Map<unknown, unknown>, key: unknown, value: unknown) {
        if (Map.prototype.has.call(this, key)) {
          return Map.prototype.get.call(this, key);
        }
        Map.prototype.set.call(this, key, value);
        return value;
      },
    });
  }
  if (typeof mapProto.getOrInsertComputed !== 'function') {
    Object.defineProperty(mapProto, 'getOrInsertComputed', {
      configurable: true,
      writable: true,
      value: function getOrInsertComputed(
        this: Map<unknown, unknown>,
        key: unknown,
        compute: (key: unknown) => unknown
      ) {
        if (Map.prototype.has.call(this, key)) {
          return Map.prototype.get.call(this, key);
        }
        const value = compute(key);
        Map.prototype.set.call(this, key, value);
        return value;
      },
    });
  }

  const weakMapProto = WeakMap.prototype as any;
  if (typeof weakMapProto.getOrInsert !== 'function') {
    Object.defineProperty(weakMapProto, 'getOrInsert', {
      configurable: true,
      writable: true,
      value: function getOrInsert(this: WeakMap<object, unknown>, key: object, value: unknown) {
        if (WeakMap.prototype.has.call(this, key)) {
          return WeakMap.prototype.get.call(this, key);
        }
        WeakMap.prototype.set.call(this, key, value);
        return value;
      },
    });
  }
  if (typeof weakMapProto.getOrInsertComputed !== 'function') {
    Object.defineProperty(weakMapProto, 'getOrInsertComputed', {
      configurable: true,
      writable: true,
      value: function getOrInsertComputed(
        this: WeakMap<object, unknown>,
        key: object,
        compute: (key: object) => unknown
      ) {
        if (WeakMap.prototype.has.call(this, key)) {
          return WeakMap.prototype.get.call(this, key);
        }
        const value = compute(key);
        WeakMap.prototype.set.call(this, key, value);
        return value;
      },
    });
  }

  const promiseCtor = Promise as PromiseConstructor & {
    try?: (fn: (...args: unknown[]) => unknown, ...args: unknown[]) => Promise<unknown>;
  };
  if (typeof promiseCtor.try !== 'function') {
    Object.defineProperty(promiseCtor, 'try', {
      configurable: true,
      writable: true,
      value: (fn: (...args: unknown[]) => unknown, ...args: unknown[]) =>
        new Promise((resolve, reject) => {
          try {
            resolve(fn(...args));
          } catch (error) {
            reject(error);
          }
        }),
    });
  }
})();

// Import Sentry test utilities in development
if (import.meta.env.DEV) {
  import('./utils/testSentry');
}

// Initialize Sentry for error tracking and performance monitoring
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.VITE_ENV === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: import.meta.env.VITE_ENV === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    environment: import.meta.env.VITE_ENV || 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sendDefaultPii: false,

    // Only enable in staging and production unless explicitly enabled
    enabled:
      import.meta.env.VITE_ENV === 'staging' ||
      import.meta.env.VITE_ENV === 'production' ||
      import.meta.env.VITE_SENTRY_ENABLED === 'true',
  });
}

// TODO: Discuss staleTime strategy
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 2,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <App />
        <PermissionRequestDialogHost />
      </AppErrorBoundary>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  </StrictMode>
);
